import { computed, shallowReactive, toValue, type ComputedRef, type MaybeRefOrGetter } from "vue";
import { tryOnScopeDispose } from "@vueuse/core";

/*****************************************************************************
 * The '(N) ' prefix on the tab title (rank 49): how many things are waiting
 * for the member across every registered source (unread alerts, unseen feed
 * rows). useAppHead's titleTemplate reads titleBadgeCount, so a source only
 * registers its count and never touches the title itself.
 *
 * Module scope, like useCommandPalette: the sources live in unrelated
 * components (the bell, the Shadow Spike feed), and there is one tab title.
 *****************************************************************************/

// Keyed per registration, so two callers passing the same ref each count, and each unregisters only its own.
const sources = shallowReactive( new Map< symbol, MaybeRefOrGetter< number > >() );

// Anything that is not a positive finite number counts as nothing, so a source still loading (NaN, undefined) never
// shows '(NaN)'.
function asCount( value: unknown ): number {
    return typeof value === 'number' && Number.isFinite( value ) && value > 0 ? Math.floor( value ) : 0;
}

/** The sum of every registered count. Reactive: it follows each source and sources joining or leaving. */
export const titleBadgeCount: ComputedRef< number > = computed( () => {
    let total = 0;
    sources.forEach( source => { total += asCount( toValue( source ) ); } );
    return total;
} );

/** A title with the badge prefix, or the title unchanged when there is nothing to count. */
export function titleWithBadge( title: string, count: number ): string {
    const badge = asCount( count );
    return badge > 0 ? `(${ badge }) ${ title }` : title;
}

/**
 * Adds `count` to the tab title's '(N) ' badge for as long as the calling scope lives (a component's setup, or an
 * effectScope). Returns the unregister function for a caller that stops counting earlier.
 */
export function useTitleBadge( count: MaybeRefOrGetter< number > ): () => void {
    const key = Symbol( 'title-badge' );
    sources.set( key, count );
    const unregister = (): void => { sources.delete( key ); };
    tryOnScopeDispose( unregister );
    return unregister;
}

/** Test helper: forget every registered count. */
export function resetTitleBadges(): void {
    sources.clear();
}
